import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../api/index.js';
import BulkSwapQuotaModal from './BulkSwapQuotaModal';
import EmptyState from './EmptyState';
import QuotaEditModal from './QuotaEditModal';
import SearchBar from './SearchBar';
import Spinner from './Spinner';
import Toast from './Toast';
import { useToast } from '../hooks/useToast';
import { exportOverQuotaUsersToExcel } from '../utils/exportOverQuotaUsers';
import {
  formatBatchResultToast,
  formatGender,
  getOrphanTerminationQuota,
  getTerminationCnqId,
  getUserCnqTypes,
  isOverQuotaQuota,
  isOverQuotaUser,
  sortUsers,
  userHasAssignedQuotas,
} from '../utils/quotaUserHelpers';
import { buildQuotaUserStore } from '../utils/quotaUserFetch';
import pageStyles from '../pages/Page.module.css';
import styles from '../pages/OverQuotaUsersPage.module.css';

function SortableHeader({ label, sortKey, activeKey, direction, onSort }) {
  const isActive = activeKey === sortKey;
  const icon = isActive ? (direction === 'asc' ? '↑' : '↓') : '↕';

  return (
    <th>
      <button
        type="button"
        className={`${styles.sortableTh} ${isActive ? styles.sortableThActive : ''}`}
        onClick={() => onSort(sortKey)}
      >
        {label}
        <span className={styles.sortIcon} aria-hidden="true">
          {icon}
        </span>
      </button>
    </th>
  );
}

const INITIAL_FILTERS = {
  cntFilter: '',
  cnqTypeFilter: '',
  quotaNameFilter: '',
  whFlagFilter: '',
  quotaAssignmentFilter: 'all',
};

const PAGE_SIZE = 100;

function isAbortError(error) {
  return error?.name === 'AbortError' || error?.name === 'TimeoutError';
}

function getLoadingLabel(phase, progress) {
  if (phase === 'processing') return 'Processing response…';
  if (phase === 'building') return 'Building table…';
  if (typeof progress === 'number' && progress >= 0) return `Downloading… ${progress}%`;
  return 'Downloading…';
}

function getLoadingHint(phase) {
  if (phase === 'processing') return 'Parsing user data — almost there.';
  if (phase === 'building') return 'Preparing the table view.';
  return 'Large studies can take 1–2 minutes on the first load.';
}

/**
 * @param {Object} props
 * @param {'over-quota' | 'reassign'} props.variant
 * @param {string} props.title
 * @param {string} props.subtitle
 * @param {(studyId: string|number) => Promise<import('../utils/quotaUserTypes.js').QuotaUsersResponse>} props.fetchUsersApi
 * @param {boolean} [props.showReviveAction]
 */
export default function QuotaUsersScreen({
  variant,
  title,
  subtitle,
  fetchUsersApi,
  showReviveAction = false,
}) {
  const [studyId, setStudyId] = useState('');
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState('');
  const [downloadProgress, setDownloadProgress] = useState(-1);
  const [page, setPage] = useState(1);
  const [reviving, setReviving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [editUser, setEditUser] = useState(null);
  const [showBulkSwap, setShowBulkSwap] = useState(false);
  const [toast, showToast] = useToast();
  const fetchAbortRef = useRef(null);
  const usersFullRef = useRef(new Map());

  useEffect(() => () => fetchAbortRef.current?.abort(), []);

  useEffect(() => {
    setPage(1);
  }, [filters, sortKey, sortDir, users]);

  const resetFilters = () => setFilters(INITIAL_FILTERS);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const fetchDataByStudyId = useCallback(
    async (id) => {
      if (!id || !String(id).trim()) {
        showToast('Enter a valid study_id', 'error');
        return;
      }

      fetchAbortRef.current?.abort();
      const controller = new AbortController();
      fetchAbortRef.current = controller;

      setLoading(true);
      setLoadingPhase('fetching');
      setDownloadProgress(-1);
      setUsers(null);
      usersFullRef.current = new Map();
      resetFilters();
      setSortKey(null);
      setSortDir('asc');
      setPage(1);
      setSelectedIds(new Set());
      setEditUser(null);
      setShowBulkSwap(false);

      try {
        const res = await fetchUsersApi(id, {
          signal: controller.signal,
          onDownloadProgress: (received, total) => {
            if (total > 0) {
              setDownloadProgress(Math.min(99, Math.round((received / total) * 100)));
            }
          },
          onProcessing: () => {
            setLoadingPhase('processing');
            setDownloadProgress(100);
          },
        });

        if (controller.signal.aborted) return;

        setLoadingPhase('building');

        const { slimUsers, fullById } = buildQuotaUserStore(res.users);
        usersFullRef.current = fullById;

        setUsers(slimUsers);
        setLoading(false);
        setLoadingPhase('');
        setDownloadProgress(-1);
      } catch (e) {
        if (isAbortError(e)) {
          if (fetchAbortRef.current === controller) {
            showToast(
              e.name === 'TimeoutError'
                ? 'Request timed out. The study may be too large — try again or contact backend.'
                : 'Fetch cancelled.',
              'error',
            );
          }
          return;
        }
        showToast(e.message, 'error');
      } finally {
        if (fetchAbortRef.current === controller) {
          setLoading(false);
          setLoadingPhase('');
          setDownloadProgress(-1);
        }
      }
    },
    [fetchUsersApi, showToast],
  );

  const fetchData = () => fetchDataByStudyId(studyId);

  const refreshUsers = useCallback(
    async (id) => {
      if (!id || !String(id).trim()) return;
      try {
        const res = await fetchUsersApi(id);
        const { slimUsers, fullById } = buildQuotaUserStore(res.users);
        usersFullRef.current = fullById;
        setUsers(slimUsers);
      } catch (e) {
        showToast(e.message || 'Failed to refresh users', 'error');
      }
    },
    [fetchUsersApi, showToast],
  );

  const filteredUsers = useMemo(() => {
    if (!users) return [];

    let list = users;
    const {
      cntFilter,
      cnqTypeFilter,
      quotaNameFilter,
      whFlagFilter,
      quotaAssignmentFilter,
    } = filters;

    const cntQuery = cntFilter.trim();
    if (cntQuery) {
      list = list.filter((user) => String(user.cnt_id).includes(cntQuery));
    }

    if (whFlagFilter) {
      list = list.filter((user) => String(user.wh_flag ?? '') === whFlagFilter);
    }

    if (cnqTypeFilter) {
      list = list.filter((user) =>
        (user.quota_data ?? []).some((quota) => String(quota.cnq_type) === cnqTypeFilter),
      );
    }

    if (quotaNameFilter) {
      list = list.filter((user) =>
        (user.quota_data ?? []).some(
          (quota) => String(quota.quota_name ?? '').trim() === quotaNameFilter,
        ),
      );
    }

    if (quotaAssignmentFilter === 'with') {
      list = list.filter((user) => userHasAssignedQuotas(user));
    } else if (quotaAssignmentFilter === 'without') {
      list = list.filter((user) => !userHasAssignedQuotas(user));
    }

    return list;
  }, [users, filters]);

  const availableWhFlags = useMemo(() => {
    if (!users) return [];
    const flags = new Set();
    users.forEach((user) => {
      if (user.wh_flag != null) flags.add(String(user.wh_flag));
    });
    return [...flags].sort((a, b) => Number(a) - Number(b));
  }, [users]);

  const availableCnqTypes = useMemo(() => {
    if (!users) return [];
    const types = new Set();
    users.forEach((user) => {
      getUserCnqTypes(user).forEach((type) => types.add(String(type)));
    });
    return [...types].sort((a, b) => Number(a) - Number(b));
  }, [users]);

  const availableQuotaNames = useMemo(() => {
    if (!users) return [];
    const names = new Set();
    users.forEach((user) => {
      (user.quota_data ?? []).forEach((quota) => {
        const name = String(quota.quota_name ?? '').trim();
        if (name) names.add(name);
      });
    });
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [users]);

  const displayUsers = useMemo(
    () => sortUsers(filteredUsers, sortKey, sortDir),
    [filteredUsers, sortKey, sortDir],
  );

  const totalPages = Math.max(1, Math.ceil(displayUsers.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const paginatedUsers = useMemo(
    () => displayUsers.slice(pageStart, pageStart + PAGE_SIZE),
    [displayUsers, pageStart],
  );
  const pageEnd = displayUsers.length === 0 ? 0 : Math.min(pageStart + PAGE_SIZE, displayUsers.length);

  const loadingLabel = getLoadingLabel(loadingPhase, downloadProgress);
  const loadingHint = getLoadingHint(loadingPhase);
  const spinnerProgress =
    loadingPhase === 'processing' || loadingPhase === 'building'
      ? 100
      : downloadProgress;

  const handleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
  };

  const filteredIds = useMemo(
    () => filteredUsers.map((user) => user.wh_mo_id),
    [filteredUsers],
  );

  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

  const toggleSelect = (whMoId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(whMoId)) next.delete(whMoId);
      else next.add(whMoId);
      return next;
    });
  };

  const handleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleRevive = async () => {
    const wh_mo_ids = Array.from(selectedIds);
    if (!wh_mo_ids.length) return;

    setReviving(true);
    try {
      const res = await api.reviveOverQuotaUsers(wh_mo_ids);
      const toastResult = formatBatchResultToast(res.message, {
        successIds: res.data?.revived,
        skipped: res.data?.skipped,
        failed: res.data?.failed,
        successLabel: 'Revived',
      });
      showToast(toastResult.text, toastResult.type);
      setSelectedIds(new Set());
      setReviving(false);
      void refreshUsers(studyId);
    } catch (e) {
      showToast(e.message || 'Revive failed', 'error');
      setReviving(false);
    }
  };

  const handleSaveQuota = async (payload, user) => {
    if (!isOverQuotaUser(user)) {
      showToast(
        'Single-user quota save is only available for over-quota users. Use bulk swap or wait for a general reassign API.',
        'error',
      );
      throw new Error('Save not available for this user');
    }

    try {
      const res = await api.reassignAndReviveOverQuotaUser(payload);
      showToast(
        res.message ||
          (res.data?.revived
            ? `User ${payload.wh_mo_id} re-assigned and revived successfully.`
            : `User ${payload.wh_mo_id} re-assigned successfully.`),
      );
      setEditUser(null);
      void refreshUsers(studyId);
    } catch (e) {
      showToast(e.message || 'Re-assign & revive failed', 'error');
      throw e;
    }
  };

  const selectedUsers = useMemo(() => {
    if (!users || selectedIds.size === 0) return [];
    return users
      .filter((user) => selectedIds.has(user.wh_mo_id))
      .map((user) => usersFullRef.current.get(user.wh_mo_id) ?? user);
  }, [users, selectedIds]);

  const handleBulkSwapQuota = async (payload) => {
    try {
      const res = await api.bulkSwapQuota(payload);
      const toastResult = formatBatchResultToast(res.message, {
        successIds: res.data?.updated,
        skipped: res.data?.skipped,
        failed: res.data?.failed,
        successLabel: 'Swapped quota for',
      });
      showToast(toastResult.text, toastResult.type);
      setShowBulkSwap(false);
      setSelectedIds(new Set());
      void refreshUsers(studyId);
    } catch (e) {
      showToast(e.message || 'Bulk swap failed', 'error');
      throw e;
    }
  };

  const selectedCount = selectedIds.size;
  const isEmpty = users && users.length === 0;
  const noFilterMatches = users?.length > 0 && filteredUsers.length === 0;
  const hasActiveFilters = Boolean(
    filters.cntFilter.trim() ||
      filters.cnqTypeFilter ||
      filters.quotaNameFilter ||
      filters.whFlagFilter ||
      filters.quotaAssignmentFilter !== 'all',
  );

  const clearFilters = () => resetFilters();

  const handleExport = () => {
    const rowsToExport =
      selectedCount > 0
        ? users.filter((user) => selectedIds.has(user.wh_mo_id))
        : users;

    if (!rowsToExport.length) {
      showToast('No users to export', 'error');
      return;
    }

    exportOverQuotaUsersToExcel(rowsToExport, studyId, { selectedOnly: selectedCount > 0 });
    showToast(
      `Exported ${rowsToExport.length} user${rowsToExport.length !== 1 ? 's' : ''} to Excel (${selectedCount > 0 ? 'selected' : 'all'}).`,
    );
  };

  const emptyStateLabel = variant === 'reassign' ? 'study (no users)' : 'study_id';

  return (
    <div className={`${pageStyles.page} ${styles.page}`}>
      <Toast {...toast} />

      <div className={pageStyles.pageHeader}>
        <h1 className={pageStyles.pageTitle}>{title}</h1>
        <p className={pageStyles.pageSubtitle}>{subtitle}</p>
      </div>

      <SearchBar
        label="Study ID (study_id)"
        placeholder="e.g. 1234"
        value={studyId}
        onChange={setStudyId}
        onFetch={fetchData}
        loading={loading}
      />

      {loading && (
        <Spinner label={loadingLabel} hint={loadingHint} progress={spinnerProgress} />
      )}

      {users && !loading && (
        <div className={pageStyles.results}>
          {isEmpty && (
            <EmptyState
              id={studyId}
              label={emptyStateLabel}
              message={
                variant === 'reassign'
                  ? `No users found for study_id: ${studyId}`
                  : undefined
              }
              hint={
                variant === 'reassign'
                  ? 'This study has no respondents yet, or the ID may be incorrect.'
                  : undefined
              }
            />
          )}

          {!isEmpty && (
            <>
              <div className={styles.toolbar}>
                <div className={styles.filters}>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel} htmlFor="cnt-filter">
                      Filter by cnt_id
                    </label>
                    <input
                      id="cnt-filter"
                      className={styles.filterInput}
                      type="text"
                      inputMode="numeric"
                      value={filters.cntFilter}
                      onChange={(e) => updateFilter('cntFilter', e.target.value)}
                      placeholder="e.g. 19302"
                    />
                  </div>
                  {variant === 'reassign' && (
                    <>
                      <div className={styles.filterGroup}>
                        <label className={styles.filterLabel} htmlFor="wh-flag-filter">
                          Filter by wh_flag
                        </label>
                        <select
                          id="wh-flag-filter"
                          className={styles.filterSelect}
                          value={filters.whFlagFilter}
                          onChange={(e) => updateFilter('whFlagFilter', e.target.value)}
                        >
                          <option value="">All flags</option>
                          {availableWhFlags.map((flag) => (
                            <option key={flag} value={flag}>
                              {flag}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.filterGroup}>
                        <label className={styles.filterLabel} htmlFor="quota-assignment-filter">
                          Assigned quotas
                        </label>
                        <select
                          id="quota-assignment-filter"
                          className={styles.filterSelect}
                          value={filters.quotaAssignmentFilter}
                          onChange={(e) => updateFilter('quotaAssignmentFilter', e.target.value)}
                        >
                          <option value="all">All users</option>
                          <option value="with">With assigned quotas</option>
                          <option value="without">Without assigned quotas</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel} htmlFor="cnq-type-filter">
                      Filter by cnq_type
                    </label>
                    <select
                      id="cnq-type-filter"
                      className={styles.filterSelect}
                      value={filters.cnqTypeFilter}
                      onChange={(e) => updateFilter('cnqTypeFilter', e.target.value)}
                    >
                      <option value="">All types</option>
                      {availableCnqTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel} htmlFor="quota-name-filter">
                      Filter by quota name
                    </label>
                    <select
                      id="quota-name-filter"
                      className={styles.filterSelect}
                      value={filters.quotaNameFilter}
                      onChange={(e) => updateFilter('quotaNameFilter', e.target.value)}
                    >
                      <option value="">All quota names</option>
                      {availableQuotaNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className={styles.stats}>
                  <span>
                    {displayUsers.length === 0 ? (
                      <>Showing <strong>0</strong> users</>
                    ) : (
                      <>
                        Showing <strong>{pageStart + 1}–{pageEnd}</strong> of{' '}
                        <strong>{displayUsers.length}</strong> filtered
                        {displayUsers.length !== users.length && (
                          <> ({users.length} total)</>
                        )}
                      </>
                    )}
                  </span>
                  {selectedCount > 0 && (
                    <span>
                      <strong>{selectedCount}</strong> selected
                    </span>
                  )}
                  {hasActiveFilters && (
                    <button type="button" className={styles.clearBtn} onClick={clearFilters}>
                      Clear filters
                    </button>
                  )}
                </div>
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.selectAllBtn}
                    onClick={handleSelectAllFiltered}
                    disabled={filteredIds.length === 0}
                  >
                    {allFilteredSelected ? 'Deselect All' : 'Select All'}
                  </button>
                  <button type="button" className={styles.exportBtn} onClick={handleExport}>
                    Export Excel
                  </button>
                  <button
                    type="button"
                    className={styles.swapBtn}
                    onClick={() => setShowBulkSwap(true)}
                    disabled={selectedCount < 1}
                  >
                    Swap Quota
                  </button>
                  {showReviveAction && (
                    <button
                      type="button"
                      className={styles.reviveBtn}
                      onClick={handleRevive}
                      disabled={selectedCount < 1 || reviving}
                    >
                      {reviving ? 'Reviving…' : 'Revive'}
                    </button>
                  )}
                </div>
              </div>

              {noFilterMatches ? (
                <div className={styles.noMatches}>No users match the current filters.</div>
              ) : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.checkboxCol}>
                          <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={allFilteredSelected}
                            onChange={handleSelectAllFiltered}
                            disabled={filteredIds.length === 0}
                            aria-label="Select all filtered users"
                          />
                        </th>
                        <th>wh_mo_id</th>
                        <th>cnt_id</th>
                        <SortableHeader
                          label="wh_flag"
                          sortKey="wh_flag"
                          activeKey={sortKey}
                          direction={sortDir}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          label="age"
                          sortKey="age"
                          activeKey={sortKey}
                          direction={sortDir}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          label="gender"
                          sortKey="gender"
                          activeKey={sortKey}
                          direction={sortDir}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          label="cnq_type"
                          sortKey="cnq_type"
                          activeKey={sortKey}
                          direction={sortDir}
                          onSort={handleSort}
                        />
                        <th>quota_data</th>
                        <th>actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.map((user) => {
                        const isSelected = selectedIds.has(user.wh_mo_id);
                        const terminationCnqId = getTerminationCnqId(user);
                        const orphanQuota = getOrphanTerminationQuota(user);
                        const overQuota = isOverQuotaUser(user);

                        return (
                          <tr
                            key={`${user.wh_mo_id}-${user.cnt_id}`}
                            className={isSelected ? styles.selectedRow : undefined}
                          >
                            <td className={styles.checkboxCol}>
                              <input
                                type="checkbox"
                                className={styles.checkbox}
                                checked={isSelected}
                                onChange={() => toggleSelect(user.wh_mo_id)}
                                aria-label={`Select wh_mo_id ${user.wh_mo_id}`}
                              />
                            </td>
                            <td className={styles.mono}>{user.wh_mo_id}</td>
                            <td className={styles.mono}>{user.cnt_id}</td>
                            <td>
                              <span
                                className={`${styles.whFlagBadge} ${user.wh_flag === 2 ? styles.whFlagOverQuota : ''}`}
                              >
                                {user.wh_flag ?? '—'}
                              </span>
                            </td>
                            <td>{user.age ?? '—'}</td>
                            <td>
                              <span className={styles.genderBadge}>
                                {formatGender(user.gender)}
                              </span>
                            </td>
                            <td>
                              <div className={styles.cnqTypeList}>
                                {getUserCnqTypes(user).map((type) => (
                                  <span key={type} className={styles.cnqTypeBadge}>
                                    {type}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td>
                              <ul className={styles.quotaList}>
                                {(user.quota_data ?? []).map((quota) => {
                                  const isOver = isOverQuotaQuota(quota, terminationCnqId);
                                  return (
                                    <li
                                      key={quota.cnq_id}
                                      className={`${styles.quotaItem} ${isOver ? styles.quotaItemOver : ''}`}
                                    >
                                      <span className={styles.quotaId}>{quota.cnq_id}</span>
                                      <span className={styles.cnqTypeBadge}>{quota.cnq_type}</span>
                                      <span className={styles.quotaName}>{quota.quota_name}</span>
                                      {isOver && (
                                        <span className={styles.overQuotaBadge}>Quota Full</span>
                                      )}
                                    </li>
                                  );
                                })}
                                {orphanQuota && (
                                  <li
                                    className={`${styles.quotaItem} ${styles.quotaItemOver} ${styles.quotaItemOrphan}`}
                                  >
                                    <span className={styles.quotaId}>{orphanQuota.cnq_id}</span>
                                    {orphanQuota.cnq_type != null && (
                                      <span className={styles.cnqTypeBadge}>
                                        {orphanQuota.cnq_type}
                                      </span>
                                    )}
                                    <span className={styles.quotaName}>
                                      {orphanQuota.quota_name?.trim() ||
                                        `Quota #${orphanQuota.cnq_id}`}
                                    </span>
                                    <span className={styles.overQuotaBadge}>Quota Full</span>
                                    <span className={styles.previousStudyTag}>Previous study</span>
                                  </li>
                                )}
                              </ul>
                            </td>
                            <td>
                              <button
                                type="button"
                                className={styles.editBtn}
                                onClick={() =>
                                setEditUser(usersFullRef.current.get(user.wh_mo_id) ?? user)
                              }
                              >
                                Re-assign Quota
                              </button>
                              {variant === 'reassign' && overQuota && (
                                <span className={styles.overQuotaRowTag}>Over quota</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {totalPages > 1 && (
                    <div className={styles.pagination}>
                      <button
                        type="button"
                        className={styles.pageBtn}
                        onClick={() => setPage((current) => Math.max(1, current - 1))}
                        disabled={safePage <= 1}
                      >
                        Previous
                      </button>
                      <span className={styles.pageInfo}>
                        Page <strong>{safePage}</strong> of <strong>{totalPages}</strong>
                      </span>
                      <button
                        type="button"
                        className={styles.pageBtn}
                        onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                        disabled={safePage >= totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {editUser && (
        <QuotaEditModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSave={(payload) => handleSaveQuota(payload, editUser)}
          canSave={isOverQuotaUser(editUser)}
          saveButtonLabel={
            isOverQuotaUser(editUser) ? 'Save & Revive' : 'Save (over-quota only)'
          }
          saveDisabledHint={
            isOverQuotaUser(editUser)
              ? null
              : 'Single-user save uses the over-quota reassign API. Use bulk swap for other users until a general reassign endpoint is available.'
          }
        />
      )}

      {showBulkSwap && (
        <BulkSwapQuotaModal
          users={selectedUsers}
          onClose={() => setShowBulkSwap(false)}
          onConfirm={handleBulkSwapQuota}
        />
      )}
    </div>
  );
}
